import {
	Stack,
	Typography,
	Button,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import {
	buttonFormSX,
	containerFormLeftSX,
	containerFormRightSX,
	mainContainerFormSX,
	marginSX,
	questionSX,
	questionsContainerSX,
	subtitleFirstSX,
	subtitleSX,
	titleSX,
} from '@/components/blocks/DpLibrary/DpSatisfactionForm/style/main';
import { useState } from 'react';
import { DpInputTextField } from '@/components/blocks/DpLibrary/DpInputTextField';
import { DpButtonForm } from '@/components/blocks/DpLibrary/DpButtonForm';
import { DpOutlinedSelect } from '@/components/blocks/DpOutlineSelect';
import { DpCheckboxSatisForm } from '@/components/blocks/DpLibrary/DpCheckSatisForm';
import {
	DPSATISFACTION_FORM_URL,
	DPSATISFACTION_FORM_CLIENT_ID,
	DPSATISFACTION_FORM_DE_EXTERNAL_KEY,
	DPSATISFACTION_FORM_ACTION,
	DPSATISFACTION_FORM_RETURN_XML,
	DPSATISFACTION_FORM_SUCCESS_URL,
	DPSATISFACTION_FORM_ERROR_URL,
} from '@/data/config/DPSATISFACTION_FORM';
import { useNotifications } from '@/data/Content/Notifications';

interface Question {
	subtitle?: string;
	question?: string;
	options?: string[];
	chooseNo?: string;
	chooseSi?: string;
	label?: string;
	name: string;
}

interface DpSatisfactionFormProps {
	title?: string;
	questions?: Question[];
}

interface Answers {
	[key: string]: string;
}

const initialAnswers: Answers = {};

export const DpSatisfactionForm = ({ title, questions }: DpSatisfactionFormProps) => {
	const [answers, setAnswers] = useState<Answers>(initialAnswers);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [privacyChecked, setPrivacyChecked] = useState<boolean>(false);
	const [privacyError, setPrivacyError] = useState<string>('');
	const { showSnackbarMessage } = useNotifications();

	const handleButtonClick = (name: string, buttonValue: string) => {
		setAnswers((prevAnswers) => ({
			...prevAnswers,
			[name]: buttonValue,
		}));

		if (errors[name]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: '',
			}));
		}
	};

	const handleSelectChange = (name: string, event: any) => {
		const value = event.target.value as string;
		setAnswers((prevAnswers) => ({
			...prevAnswers,
			[name]: value,
		}));
		if (errors[name]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: '',
			}));
		}
	};

	const handleInputChange = (name: string, event: any) => {
		const value = event.target.value;
		setAnswers((prevAnswers) => ({
			...prevAnswers,
			[name]: value,
		}));
		if (errors[name]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: '',
			}));
		}
	};

	const handlePrivacyCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPrivacyChecked(event.target.checked);
		if (event.target.checked) {
			setPrivacyError('');
		}
	};

	const handleCloseSuccessModal = () => {
		setSuccessModalOpen(false);
		setPrivacyChecked(false);
		setPrivacyError('');
		setAnswers({});
		setErrors({});
	};

	const isFormComplete = () => {
		const requiredFields = [
			'experiencia_general',
			'facilidad_navegacion',
			'informacion_producto',
			'diseno_visual',
			'velocidad_carga',
			'problema_tecnico',
			'proceso_compra',
			'seguridad_compra',
			'gusta_mas',
			'gusta_menos',
			'sugerencia_mejora',
			'correo',
		];

		const newErrors: { [key: string]: string } = {};

		requiredFields.forEach((field) => {
			if (!answers[field]) {
				newErrors[field] = 'Este campo es requerido';
			}
		});

		Object.keys(answers).forEach((field) => {
			if (
				(answers[field] === 'no' && field !== 'problema_tecnico') ||
				(answers[field] === 'si' && field === 'problema_tecnico')
			) {
				if (!answers[`${field}_specific`]) {
					newErrors[`${field}_specific`] = 'Este campo es requerido';
				}
			}
		});

		Object.keys(answers).forEach((field) => {
			if (answers[field]?.includes('Otra')) {
				if (!answers[`${field}_specific`]) {
					newErrors[`${field}_specific`] = 'Este campo es requerido';
				}
			}
		});

		setErrors(newErrors);

		return Object.keys(newErrors).length === 0;
	};

	const onMessageError = (error?: string) => {
		showSnackbarMessage(
			error || 'Ocurrió un error al enviar el formulario. Por favor, inténtelo de nuevo.'
		);
	};

	const handleSubmitClick = async () => {
		if (!isFormComplete() || !privacyChecked) {
			if (!privacyChecked) {
				setPrivacyError('Debes aceptar el aviso de privacidad para continuar.');
			}
			setModalOpen(true);
			return;
		}

		const body = new URLSearchParams({
			_clientID: DPSATISFACTION_FORM_CLIENT_ID,
			_deExternalKey: DPSATISFACTION_FORM_DE_EXTERNAL_KEY,
			_action: DPSATISFACTION_FORM_ACTION,
			_returnXML: DPSATISFACTION_FORM_RETURN_XML,
			_successURL: DPSATISFACTION_FORM_SUCCESS_URL,
			_errorURL: DPSATISFACTION_FORM_ERROR_URL,
			...answers,
		}).toString();

		try {
			const response = await fetch(DPSATISFACTION_FORM_URL, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body,
			});

			if (response.ok) {
				setSuccessModalOpen(true);
				return;
			}
			onMessageError();
		} catch {
			onMessageError();
		} finally {
			setAnswers(initialAnswers);
		}
	};

	return (
		<Stack sx={mainContainerFormSX}>
			{title ? (
				<Typography component="h1" sx={titleSX}>
					{title}
				</Typography>
			) : null}
			<Stack sx={questionsContainerSX}>
				<Stack sx={containerFormRightSX}>
					{questions
						? questions
								.slice(0, 6)
								.map((qt: Question, index: number) => (
									<QuestionComponent
										key={index}
										qt={qt}
										answers={answers}
										errors={errors}
										handleSelectChange={handleSelectChange}
										handleInputChange={handleInputChange}
										handleButtonClick={handleButtonClick}
										index={index}
									/>
								))
						: null}
				</Stack>
				<Stack sx={containerFormLeftSX}>
					{questions
						? questions
								.slice(6)
								.map((qt: Question, index: number) => (
									<QuestionComponent
										key={index}
										qt={qt}
										answers={answers}
										errors={errors}
										handleSelectChange={handleSelectChange}
										handleInputChange={handleInputChange}
										handleButtonClick={handleButtonClick}
										index={index}
									/>
								))
						: null}
				</Stack>
			</Stack>
			<Stack
				sx={{
					width: '100%',
					margin: '5px 0',
					background: '#C9C9C9',
					height: '1px',
					marginBottom: '5px',
				}}
			></Stack>

			<DpCheckboxSatisForm checked={privacyChecked} onChange={handlePrivacyCheckboxChange}>
				<Typography
					component="span"
					sx={{ fontSize: 'var(--font-size-default)', paddingTop: '4px' }}
				>
					{'He leído y consentido el'}{' '}
					<Typography
						component="a"
						type="link"
						href="https://dpcard.mx/aviso-privacidad"
						target="_blank"
						sx={{ color: '#011688', fontSize: 'var(--font-size-default)', textDecoration: 'none' }}
					>
						{'Aviso de privacidad'}
					</Typography>
				</Typography>
			</DpCheckboxSatisForm>
			{privacyError ? (
				<Typography
					sx={{ color: '#D32F2F', fontSize: 'var(--font-size-caption)', marginBottom: '5px' }}
				>
					{privacyError}
				</Typography>
			) : null}
			<DpButtonForm label="Enviar" onClick={handleSubmitClick} />
			<Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
				<DialogTitle>{'Formulario Incompleto'}</DialogTitle>
				<DialogContent>
					<Typography>
						{'Por favor, complete todas las respuestas antes de enviar el formulario.'}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => (window.location.href = 'https://www.dportenis.mx')}
						color="primary"
					>
						{'Ir a Dportenis'}
					</Button>
					<Button onClick={() => setModalOpen(false)} color="primary">
						{'Volver'}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={successModalOpen} onClose={handleCloseSuccessModal}>
				<DialogTitle>{'¡Encuesta enviada con éxito!'}</DialogTitle>
				<DialogContent>
					<Typography>{'Tu encuesta se ha enviado con éxito.'}</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => (window.location.href = 'https://www.dportenis.mx')}
						color="primary"
					>
						{'Ir a Dportenis'}
					</Button>
					<Button onClick={handleCloseSuccessModal} color="primary">
						{'Cerrar'}
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
};

const renderTitle = (subtitle: string | undefined, index: number) =>
	subtitle ? (
		<Typography component="h2" sx={index === 0 ? subtitleFirstSX : subtitleSX}>
			{subtitle}
		</Typography>
	) : null;

const renderQuestion = (question: string | undefined) =>
	question ? (
		<Typography component="h3" sx={questionSX}>
			{question}
		</Typography>
	) : null;

const renderOptions = (
	qt: Question,
	answers: { [key: string]: string },
	errors: { [key: string]: string },
	handleSelectChange: (name: string, event: any) => void,
	handleInputChange: (name: string, event: any) => void
) =>
	qt.options ? (
		<>
			<DpOutlinedSelect
				required
				value={answers[qt.name] || ''}
				onChange={(event) => handleSelectChange(qt.name, event)}
				error={!!errors[qt.name]}
			>
				{qt.options.map((op: any, idx: any) => (
					<MenuItem key={idx} value={op}>
						{op}
					</MenuItem>
				))}
			</DpOutlinedSelect>
			{errors[qt.name] ? (
				<Typography
					sx={{ color: '#D32F2F', fontSize: 'var(--font-size-caption)', marginTop: '5px' }}
				>
					{errors[qt.name]}
				</Typography>
			) : null}
			{answers[qt.name]?.includes('Otra') ? (
				<DpInputTextField
					sx={{ marginTop: '15px' }}
					label="Especifica tu respuesta"
					value={answers[`${qt.name}_specific`] || ''}
					onChange={(event) => handleInputChange(`${qt.name}_specific`, event)}
					error={!!errors[`${qt.name}_specific`]}
					helperText={errors[`${qt.name}_specific`] || ''}
				/>
			) : null}
		</>
	) : null;

const renderYesNoButtonGroup = (
	qt: Question,
	answers: { [key: string]: string },
	errors: { [key: string]: string },
	handleButtonClick: (name: string, value: string) => void
) => (
	<Stack direction="row">
		<Button
			sx={{
				...buttonFormSX,
				'&.MuiButton-outlined': {
					borderColor: errors[qt.name] ? '#D32F2F' : '#C9C9C9',
					'&:hover': {
						backgroundColor: '#FFF',
						borderColor: '#011688',
					},
					'&:active': {
						borderColor: '#011688',
					},
				},
			}}
			variant={answers[qt.name] === 'si' ? 'contained' : 'outlined'}
			onClick={() => handleButtonClick(qt.name, 'si')}
		>
			{qt.chooseSi}
		</Button>
		<Button
			sx={{
				...buttonFormSX,
				'&.MuiButton-outlined': {
					borderColor: errors[qt.name] ? '#D32F2F' : '#C9C9C9',
					'&:hover': {
						backgroundColor: '#FFF',
						borderColor: '#011688',
					},
					'&:active': {
						borderColor: '#011688',
					},
				},
			}}
			variant={answers[qt.name] === 'no' ? 'contained' : 'outlined'}
			onClick={() => handleButtonClick(qt.name, 'no')}
		>
			{qt.chooseNo}
		</Button>
	</Stack>
);

const renderConditionalInputField = (
	qt: Question,
	answers: { [key: string]: string },
	errors: { [key: string]: string },
	handleInputChange: (name: string, event: any) => void
) => {
	if (
		(answers[qt.name] === 'no' && qt.name !== 'problema_tecnico') ||
		(answers[qt.name] === 'si' && qt.name === 'problema_tecnico')
	) {
		return (
			<DpInputTextField
				sx={{ marginTop: '15px' }}
				label="Especifica tu respuesta"
				value={answers[`${qt.name}_specific`] || ''}
				onChange={(event) => handleInputChange(`${qt.name}_specific`, event)}
				error={!!errors[`${qt.name}_specific`]}
				helperText={errors[`${qt.name}_specific`] || ''}
			/>
		);
	}
	return null;
};

const renderYesNoButtons = (
	qt: Question,
	answers: { [key: string]: string },
	errors: { [key: string]: string },
	handleButtonClick: (name: string, value: string) => void,
	handleInputChange: (name: string, event: any) => void
) => {
	if (!qt.chooseNo || !qt.chooseSi) return null;

	return (
		<>
			{renderYesNoButtonGroup(qt, answers, errors, handleButtonClick)}
			{errors[qt.name] ? (
				<Typography
					sx={{ color: '#D32F2F', fontSize: 'var(--font-size-caption)', marginTop: '5px' }}
				>
					{errors[qt.name]}
				</Typography>
			) : null}
			{renderConditionalInputField(qt, answers, errors, handleInputChange)}
		</>
	);
};

const renderLabelField = (
	qt: Question,
	answers: { [key: string]: string },
	errors: { [key: string]: string },
	handleInputChange: (name: string, event: any) => void
) =>
	qt.label ? (
		<DpInputTextField
			label={qt.label}
			value={answers[qt.name] || ''}
			onChange={(event) => handleInputChange(qt.name, event)}
			error={!!errors[qt.name]}
			helperText={errors[qt.name]}
		/>
	) : null;

const QuestionComponent = ({
	qt,
	answers,
	errors,
	handleSelectChange,
	handleInputChange,
	handleButtonClick,
	index,
}: {
	qt: Question;
	answers: { [key: string]: string };
	errors: { [key: string]: string };
	handleSelectChange: (name: string, event: any) => void;
	handleInputChange: (name: string, event: any) => void;
	handleButtonClick: (name: string, value: string) => void;
	index: number;
}) => (
	<Stack sx={marginSX}>
		{renderTitle(qt.subtitle, index)}
		{renderQuestion(qt.question)}
		{renderOptions(qt, answers, errors, handleSelectChange, handleInputChange)}
		{renderYesNoButtons(qt, answers, errors, handleButtonClick, handleInputChange)}
		{renderLabelField(qt, answers, errors, handleInputChange)}
	</Stack>
);
